import React, { useEffect, useMemo, useState } from 'react';
import {
    Box, Typography, IconButton, TextField, MenuItem, Button, CircularProgress,
    FormControlLabel, Checkbox, Divider, Alert,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import { usePagosInmuebles } from '../../hooks/usePagosInmuebles';
import { IPagoInmuebleListItem, IServicioCatalogo, MEDIOS_PAGO, IRegistrarPagoRequest } from '../../models/pagosInmuebles';

interface RegistrarPagoProps {
    casaItem: IPagoInmuebleListItem;
    onBack: () => void;
    onGuardado: () => void;
}

const hoyISO = () => new Date().toISOString().slice(0, 10);

export const RegistrarPago = ({ casaItem, onBack, onGuardado }: RegistrarPagoProps) => {
    const { getServicios, registrarPago, aprobarPago } = usePagosInmuebles();

    const [servicios, setServicios] = useState<IServicioCatalogo[]>([]);
    const [loadingCatalogos, setLoadingCatalogos] = useState(true);
    const [guardando, setGuardando] = useState(false);
    const [mensaje, setMensaje] = useState<{ tipo: 'success' | 'error'; texto: string } | null>(null);

    const [form, setForm] = useState({
        servicio: 0,
        fecha: hoyISO(),
        fecha_Operacion: hoyISO(),
        medio_Pago: 1,
        boleta: '',
        emisor: '',
        saldo_Anterior: casaItem.saldo,
        cuota: 0,
        cuota_Extra: 0,
        agua: 0,
        mora: 0,
        multa: 0,
        otros: 0,
        amenidades: 0,
        nota: '',
        banco: '',
        cuenta_Banco: '',
        tipo_Documento: '',
        aprobarInmediato: false,
    });

    useEffect(() => {
        (async () => {
            setLoadingCatalogos(true);
            const data = await getServicios();
            if (data) {
                setServicios(data);
                if (data.length > 0) {
                    setForm(f => ({ ...f, servicio: data[0].servicio }));
                }
            }
            setLoadingCatalogos(false);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const total = useMemo(() => {
        const { saldo_Anterior, cuota, cuota_Extra, agua, mora, multa, otros, amenidades } = form;
        return [saldo_Anterior, cuota, cuota_Extra, agua, mora, multa, otros, amenidades]
            .reduce((acc, v) => acc + (Number(v) || 0), 0);
    }, [form]);

    const handleNumChange = (field: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => {
        setForm(f => ({ ...f, [field]: e.target.value === '' ? 0 : Number(e.target.value) }));
    };

    const handleTextChange = (field: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => {
        setForm(f => ({ ...f, [field]: e.target.value }));
    };

    const handleGuardar = async () => {
        if (!form.boleta.trim()) {
            setMensaje({ tipo: 'error', texto: 'No. Boleta es requerido.' });
            return;
        }
        if (total <= 0) {
            setMensaje({ tipo: 'error', texto: 'El total debe ser mayor a 0.' });
            return;
        }

        setGuardando(true);
        setMensaje(null);

        const payload: IRegistrarPagoRequest = {
            casa: casaItem.casa,
            servicio: form.servicio,
            fecha: form.fecha,
            fecha_Operacion: form.fecha_Operacion,
            medio_Pago: form.medio_Pago,
            boleta: form.boleta.toUpperCase(),
            emisor: form.emisor.toUpperCase(),
            saldo_Anterior: form.saldo_Anterior,
            cuota: form.cuota,
            cuota_Extra: form.cuota_Extra,
            agua: form.agua,
            mora: form.mora,
            multa: form.multa,
            otros: form.otros,
            amenidades: form.amenidades,
            valor_Total: total,
            nota: form.nota,
            archivo: '',
            uuid_Dte: '',
            banco: form.banco,
            cuenta_Banco: form.cuenta_Banco,
            tipo_Documento: form.tipo_Documento,
            estado: 1,
        };

        const res = await registrarPago(payload);

        if (res.success && form.aprobarInmediato) {
            const resAprobar = await aprobarPago({ agencia: 0, casa: casaItem.casa, id_Abono: res.id_Abono });
            if (!resAprobar.success) {
                setMensaje({ tipo: 'error', texto: `Pago registrado pero no se pudo aprobar: ${resAprobar.message}` });
                setGuardando(false);
                return;
            }
        }

        setGuardando(false);

        if (res.success) {
            setMensaje({ tipo: 'success', texto: 'Pago registrado correctamente.' });
            setTimeout(() => onGuardado(), 900);
        } else {
            setMensaje({ tipo: 'error', texto: res.message });
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: '#f4f6f9', pb: 6 }}>
            <Box sx={{
                background: 'linear-gradient(135deg, #013467 0%, #0a4a8a 100%)',
                px: 2, py: 3, mb: 3, display: 'flex', alignItems: 'center', gap: 1,
            }}>
                <IconButton onClick={onBack} sx={{ color: '#fff' }}>
                    <ArrowBackIcon />
                </IconButton>
                <Box>
                    <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700 }}>
                        Registrar Pago
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)' }}>
                        {casaItem.num_Casa} - {casaItem.nombre}
                    </Typography>
                </Box>
            </Box>

            {loadingCatalogos ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Box sx={{ px: 2, maxWidth: 600, mx: 'auto' }}>
                    {mensaje && (
                        <Alert severity={mensaje.tipo} sx={{ mb: 2 }}>{mensaje.texto}</Alert>
                    )}

                    <Box sx={{ backgroundColor: '#fff', borderRadius: 2, p: 2.5, mb: 2, boxShadow: '0 2px 8px rgba(1,52,103,0.08)' }}>
                        <Typography variant="subtitle2" sx={{ color: '#013467', fontWeight: 700, mb: 2 }}>
                            Información General
                        </Typography>
                        <Box sx={{ display: 'grid', gap: 2 }}>
                            <TextField select label="Servicio" size="small" value={form.servicio} onChange={handleNumChange('servicio')}>
                                {servicios.map(s => (
                                    <MenuItem key={s.servicio} value={s.servicio}>{s.nombre}</MenuItem>
                                ))}
                            </TextField>
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                                <TextField label="Fecha" type="date" size="small" InputLabelProps={{ shrink: true }} value={form.fecha} onChange={handleTextChange('fecha')} />
                                <TextField label="Fecha en Banco" type="date" size="small" InputLabelProps={{ shrink: true }} value={form.fecha_Operacion} onChange={handleTextChange('fecha_Operacion')} />
                            </Box>
                        </Box>
                    </Box>

                    <Box sx={{ backgroundColor: '#fff', borderRadius: 2, p: 2.5, mb: 2, boxShadow: '0 2px 8px rgba(1,52,103,0.08)' }}>
                        <Typography variant="subtitle2" sx={{ color: '#013467', fontWeight: 700, mb: 2 }}>
                            Desglose de Valores
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                            <TextField label="Saldo Anterior" type="number" size="small" value={form.saldo_Anterior} onChange={handleNumChange('saldo_Anterior')} />
                            <TextField label="Cuota" type="number" size="small" value={form.cuota} onChange={handleNumChange('cuota')} />
                            <TextField label="Cuota Extra" type="number" size="small" value={form.cuota_Extra} onChange={handleNumChange('cuota_Extra')} />
                            <TextField label="Agua" type="number" size="small" value={form.agua} onChange={handleNumChange('agua')} />
                            <TextField label="Mora" type="number" size="small" value={form.mora} onChange={handleNumChange('mora')} />
                            <TextField label="Otros" type="number" size="small" value={form.otros} onChange={handleNumChange('otros')} />
                            <TextField label="Amenidades" type="number" size="small" value={form.amenidades} onChange={handleNumChange('amenidades')} />
                            <TextField label="Multa" type="number" size="small" value={form.multa} onChange={handleNumChange('multa')} />
                        </Box>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="h6" sx={{ color: '#013467', fontWeight: 700, textAlign: 'right' }}>
                            Total: Q. {total.toFixed(2)}
                        </Typography>
                    </Box>

                    <Box sx={{ backgroundColor: '#fff', borderRadius: 2, p: 2.5, mb: 2, boxShadow: '0 2px 8px rgba(1,52,103,0.08)' }}>
                        <Typography variant="subtitle2" sx={{ color: '#013467', fontWeight: 700, mb: 2 }}>
                            Detalles del Pago
                        </Typography>
                        <Box sx={{ display: 'grid', gap: 2 }}>
                            <TextField select label="Medio de Pago" size="small" value={form.medio_Pago} onChange={handleNumChange('medio_Pago')}>
                                {MEDIOS_PAGO.map(m => (
                                    <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                                ))}
                            </TextField>
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                                <TextField label="No. Boleta" size="small" value={form.boleta} onChange={handleTextChange('boleta')} />
                                <TextField label="Emisor" size="small" value={form.emisor} onChange={handleTextChange('emisor')} />
                            </Box>
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
                                <TextField label="Banco" size="small" value={form.banco} onChange={handleTextChange('banco')} />
                                <TextField label="Cuenta" size="small" value={form.cuenta_Banco} onChange={handleTextChange('cuenta_Banco')} />
                                <TextField label="Tipo Doc." size="small" value={form.tipo_Documento} onChange={handleTextChange('tipo_Documento')} />
                            </Box>
                        </Box>
                    </Box>

                    <Box sx={{ backgroundColor: '#fff', borderRadius: 2, p: 2.5, mb: 2, boxShadow: '0 2px 8px rgba(1,52,103,0.08)' }}>
                        <TextField
                            label="Nota"
                            multiline
                            rows={3}
                            fullWidth
                            size="small"
                            value={form.nota}
                            onChange={handleTextChange('nota')}
                        />
                        <FormControlLabel
                            sx={{ mt: 1 }}
                            control={
                                <Checkbox
                                    checked={form.aprobarInmediato}
                                    onChange={(e) => setForm(f => ({ ...f, aprobarInmediato: e.target.checked }))}
                                />
                            }
                            label="Aprobar de inmediato"
                        />
                    </Box>

                    <Button
                        fullWidth
                        variant="contained"
                        startIcon={guardando ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                        onClick={handleGuardar}
                        disabled={guardando}
                        sx={{ textTransform: 'none', fontWeight: 600, backgroundColor: '#013467', py: 1.2, '&:hover': { backgroundColor: '#0a4a8a' } }}
                    >
                        Guardar Pago
                    </Button>
                </Box>
            )}
        </Box>
    );
};
